import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Droplet, Flame, CircleCheck, Compass, ChevronDown, ChevronUp } from "lucide-react";
import { portalApi } from "@/lib/portalApi";
import { usePortalAuth } from "@/context/PortalAuth";
import { MOVEMENTS } from "./Journey";
import { BADGE_ICONS } from "./badgeIcons";

const MILESTONES = [
  { drops: 25, name: "Vessel Tilted" },
  { drops: 100, name: "First Month Poured" },
  { drops: 250, name: "Halfway Emptied" },
  { drops: 500, name: "Fully Outpoured" },
];

function nextMilestone(drops) {
  return MILESTONES.find((m) => drops < m.drops) || MILESTONES[MILESTONES.length - 1];
}

export default function FormationDashboard() {
  const { user } = usePortalAuth();
  const [p, setP] = useState(null);
  const [badges, setBadges] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showGuide, setShowGuide] = useState(false);

  useEffect(() => {
    portalApi.get("/portal/progress").then((r) => setP(r.data)).catch(() => {}).finally(() => setLoading(false));
    portalApi.get("/portal/badges/me").then((r) => setBadges(r.data)).catch(() => {});
  }, []);

  const drops = p?.living_water ?? 0;
  const goal = nextMilestone(drops);
  const pct = Math.min(100, Math.round((drops / goal.drops) * 100));
  const currentWeek = p?.current_week;

  const Stat = ({ icon: Icon, value, label, testid }) => (
    <div data-testid={testid} className="bg-white/60 border border-[#E0D8C9] rounded-xl p-6">
      <Icon className="w-6 h-6 text-[#A9724E] mb-4" strokeWidth={1.5} />
      <p className="font-serif-display text-3xl text-[#2A2825]">{value}</p>
      <p className="text-sm text-[#5E4B3C] font-body mt-0.5">{label}</p>
    </div>
  );

  return (
    <div data-testid="portal-formation">
      <p className="tracking-overline text-xs uppercase text-[#A9724E] mb-3">A record of showing up — not achievement</p>
      <h1 className="font-serif-display font-light text-4xl md:text-5xl mb-3">Living Water</h1>
      <p className="text-[#5E4B3C] font-body max-w-2xl mb-6 leading-relaxed">
        There's no one to beat here. Living Water gathers each time you show up and pour out — a quiet trace of a life being reformed.
      </p>

      <button data-testid="lw-guide-toggle" onClick={() => setShowGuide((s) => !s)} className="inline-flex items-center gap-2 text-sm text-[#A9724E] hover:underline mb-8">
        {showGuide ? <ChevronUp className="w-4 h-4" strokeWidth={1.5} /> : <ChevronDown className="w-4 h-4" strokeWidth={1.5} />}
        What do these mean? A gentle guide
      </button>

      {showGuide && (
        <div data-testid="lw-guide" className="bg-[#EAE3D9]/70 border border-[#E0D8C9] rounded-xl p-6 md:p-8 mb-10 space-y-5">
          {[
            ["Living Water drops", "Each time you show up and pour out, a drop gathers. Not points. Not a score. A visible trace of faithfulness — earned by completing a challenge, sharing in the wells, or showing up on a streak."],
            ["Garden growth", "You're a garden being watered. Every drop grows something. This is your visual proof of transformation over 24 weeks."],
            ["Streaks", "Consecutive days you came back. Not perfection — presence. What matters is that you returned."],
            ["Milestones & badges", "Quiet acknowledgments of faithful growth. They unlock on their own as you reach spiritual markers — never a leaderboard, only a mirror of who you're becoming."],
          ].map(([t, b]) => (
            <div key={t}><p className="font-serif-display text-lg text-[#2A2825] mb-1">{t}</p><p className="text-sm text-[#5E4B3C] font-body leading-relaxed">{b}</p></div>
          ))}
        </div>
      )}

      {loading ? (
        <p className="text-[#8a7a6a] font-body">Gathering…</p>
      ) : (
        <div className="space-y-6 max-w-4xl">
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
            <Stat testid="stat-drops" icon={Droplet} value={drops} label="Living Water drops" />
            <Stat testid="stat-streak" icon={Flame} value={`${p?.streak ?? 0}${(p?.streak ?? 0) === 1 ? " day" : " days"}`} label="showing-up streak" />
            <Stat testid="stat-assignments" icon={CircleCheck} value={p?.assignments_completed ?? 0} label="practices poured out" />
            <Stat testid="stat-week" icon={Compass} value={currentWeek ? `Week ${currentWeek}` : "—"} label={p?.current_movement ? p.current_movement.name : "not yet begun"} />
          </div>

          {/* milestone progress */}
          <div data-testid="milestone-progress" className="bg-white/60 border border-[#E0D8C9] rounded-xl p-6 md:p-8">
            <div className="flex items-baseline justify-between mb-3">
              <p className="font-serif-display text-xl text-[#2A2825]">Toward “{goal.name}”</p>
              <p className="text-sm text-[#8a7a6a] font-body">{drops} / {goal.drops} drops</p>
            </div>
            <div className="h-2 rounded-full bg-[#E0D8C9] overflow-hidden">
              <div className="h-full rounded-full bg-[#A9724E] transition-all duration-700" style={{ width: `${pct}%` }} />
            </div>
            <p className="text-sm text-[#5E4B3C] font-body mt-4">
              {(p?.cohort_showed_up ?? 0)} of {(p?.cohort_total ?? 0)} vessels showed up this week. You're in good company.
            </p>
          </div>

          {/* badges */}
          <div data-testid="formation-badges" className="bg-white/60 border border-[#E0D8C9] rounded-xl p-6 md:p-8">
            <div className="flex items-baseline justify-between mb-5">
              <p className="tracking-overline text-[10px] uppercase text-[#8a7a6a]">Badges · {badges.filter((b) => b.earned).length} of {badges.length}</p>
              <Link to="/portal/me" className="text-xs text-[#A9724E] font-body hover:underline">View on your profile →</Link>
            </div>
            <div className="grid grid-cols-3 sm:grid-cols-4 gap-4">
              {badges.map((b) => {
                const Icon = BADGE_ICONS[b.icon] || Droplet;
                return (
                  <div key={b.key} data-testid={`formation-badge-${b.key}`} title={b.desc}
                    className={`rounded-xl border p-3 text-center transition-colors ${b.earned ? "bg-[#EAE3D9] border-[#D8CEBE]" : "bg-white/40 border-[#E0D8C9] opacity-45"}`}>
                    <Icon className={`w-6 h-6 mx-auto mb-1.5 ${b.earned ? "text-[#A9724E]" : "text-[#b8ad9c]"}`} strokeWidth={1.5} />
                    <p className="font-serif-display text-xs text-[#2A2825] leading-tight">{b.name}</p>
                  </div>
                );
              })}
            </div>
          </div>

          {/* movement path */}
          <div className="bg-white/60 border border-[#E0D8C9] rounded-xl p-6 md:p-8">
            <p className="tracking-overline text-[10px] uppercase text-[#8a7a6a] mb-5">Where you are in the pouring</p>
            <div className="space-y-3">
              {MOVEMENTS.map((m, i) => {
                const [start, end] = m.weeks.replace("Weeks ", "").split("–").map((x) => parseInt(x, 10));
                const active = currentWeek >= start && currentWeek <= end;
                const done = currentWeek > end;
                return (
                  <div key={m.n} className="flex items-center gap-4">
                    <span className={`w-2.5 h-2.5 rounded-full shrink-0 ${active ? "bg-[#A9724E]" : done ? "bg-[#8a7a6a]" : "bg-[#E0D8C9]"}`} />
                    <span className={`font-serif-display text-lg ${active ? "text-[#A9724E]" : done ? "text-[#5E4B3C]" : "text-[#b8ad9c]"}`}>{m.name}</span>
                    <span className="text-xs text-[#8a7a6a] font-body">{m.weeks}</span>
                    {active && <span className="text-[10px] tracking-overline uppercase text-[#A9724E]">you're here</span>}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
